import React from 'react';
import { Box, Typography, Stack, Button, alpha } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { SIDEBAR_WIDTH, CELL_HEIGHT, MAX_VISIBLE } from '../constants';
import ReservationCard from './ReservationCard';

// 1. Dictionnaire de traduction des statuts API en Français
export const STATUS_LABELS = {
  PENDING: 'En attente',
  CANCELLED: 'Annulé',
  APPROVED: 'Confirmé',
  COMPLETED: 'Terminé',
};

// 2. Dictionnaire des couleurs associées aux statuts
export const STATUS_COLORS = {
  PENDING: '#F59E0B',   // Orange (En attente)
  CANCELLED: '#EF4444', // Rouge (Annulé)
  APPROVED: '#10B981',  // Vert (Confirmé / Approuvé)
  COMPLETED: '#6366F1', // Violet / Bleu (Terminé)
  DEFAULT: '#64748B',   // Gris par défaut
};

// Fonction Helper pour récupérer le libellé en français
export const getStatusLabel = (status) => {
  if (!status) return '';
  const upperStatus = String(status).toUpperCase();
  return STATUS_LABELS[upperStatus] || status;
};

// Fonction Helper pour récupérer la couleur du statut
export const getStatusColor = (status) => {
  if (!status) return STATUS_COLORS.DEFAULT;
  const upperStatus = String(status).toUpperCase();
  return STATUS_COLORS[upperStatus] || STATUS_COLORS.DEFAULT;
};

const OfferRow = ({
  offre,
  joursTimeline,
  reservations,
  aujourdhuiStr,
  largeurCellule,
  isIntervenant,
  onReservationClick,
  onOpenDay,
}) => {
  // Déterminer la couleur de la ligne selon l'intervenantId :
  // - intervenantId === 3 -> Intervenant (Bleu clair #60A5FA)
  // - intervenantId === 4 -> Praticien (Rose clair #F472B6)
  const getThemeColor = () => {
    if (offre?.intervenantId === 3) return '#60A5FA'; // Bleu clair
    if (offre?.intervenantId === 4) return '#F472B6'; // Rose clair
    return offre?.color || '#4318FF';                 // Couleur fallback
  };

  const themeColor = getThemeColor();

  return (
    <Box sx={{ display: 'flex', minWidth: 'max-content' }}>
      {/* Colonne offre (sticky à gauche) */}
      <Box
        sx={{
          minWidth: SIDEBAR_WIDTH,
          position: 'sticky',
          left: 0,
          zIndex: 30,
          bgcolor: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          borderRight: '2px solid #E0E5F2',
          borderBottom: '1px solid #F4F7FE',
        }}
      >
        {/* Indication visuelle de couleur */}
        <Box
          sx={{
            width: 4,
            height: 24,
            bgcolor: themeColor,
            borderRadius: 1,
            mr: 2,
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <Typography sx={{ fontWeight: 700, color: '#1B254B', fontSize: '0.9rem' }}>
            {offre.nom}
          </Typography>

          {offre.intervenant && (
            <Typography sx={{ fontWeight: 400, color: '#707EAE', fontSize: '0.85rem' }}>
              {offre.intervenant}
            </Typography>
          )}

          <Button
            size="small"
            onClick={() =>
              onOpenDay(
                offre,
                joursTimeline.find((j) => j.isoStr === aujourdhuiStr)
              )
            }
            startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
            sx={{
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontSize: '0.7rem',
              fontWeight: 700,
              px: 2,
              py: 0.6,
              borderRadius: '8px',
              bgcolor: alpha(themeColor, 0.1),
              border: `1px solid ${alpha(themeColor, 0.3)}`,
              color: themeColor,
              transition: '0.2s',
              '&:hover': {
                bgcolor: alpha(themeColor, 0.2),
                border: `1px solid ${themeColor}`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            Planning du jour
          </Button>
        </Box>
      </Box>

      {/* Cellules des jours dans le timeline */}
      {joursTimeline.map((jour) => {
        const cellReservations = reservations.filter(
          (res) =>
            String(res.offreId) === String(offre.id) &&
            res.dateKey === jour.isoStr
        );
        const visible = cellReservations.slice(0, MAX_VISIBLE);
        const hidden = cellReservations.slice(MAX_VISIBLE);

        return (
          <Box
            key={jour.isoStr}
            sx={{
              minWidth: largeurCellule,
              minHeight: CELL_HEIGHT,
              borderRight: '1px solid #F4F7FE',
              borderBottom: '1px solid #F4F7FE',
              position: 'relative',
            }}
          >
            <Stack spacing={0.5} sx={{ p: 0.5 }}>
              {visible.map((res) => {
                const status = res.status || res.statut;
                const statusFr = getStatusLabel(status);
                const statusColor = getStatusColor(status);

                return (
                  <ReservationCard
                    key={res.id}
                    reservation={{
                      ...res,
                      statusFr, // Transmet le libellé en Français
                    }}
                    color={statusColor} // Transmet la couleur selon le statut
                    themeColor={themeColor} // Transmet la couleur du rôle (Rose/Bleu) au besoin
                    isIntervenant={isIntervenant}
                    onClick={() => onReservationClick(res, offre)}
                  />
                );
              })}

              {hidden.length > 0 && (
                <Button size="small" onClick={() => onOpenDay(offre, jour)}>
                  +{hidden.length} autres
                </Button>
              )}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
};

export default OfferRow;